// @ts-nocheck
// Common_Search_Component.tsx — copied verbatim from sk-voice-fe
// (packages/src/Components/Common_Search_Component/Common_Search_Component.tsx).
// On web, `react-native-vector-icons/Ionicons` is aliased to src/utils/IconStub.

import React, { useState, useRef } from "react";
import {
  View,
  TextInput,
  StyleSheet,
  TouchableOpacity,
  Platform,
  Animated,
} from "react-native";
import Icon from "react-native-vector-icons/Ionicons";
import { useResponsive } from "../../theme/responsive";
import { Colors } from "../../theme/tokens";

/* ─────────────────────────────────────────
   Props
 ───────────────────────────────────────── */
type Props = Readonly<{
  placeholder?: string;
  value: string;
  onChange: (text: string) => void;
  onSubmit?: () => void;
  onFocus?: () => void;
  isDark?: boolean;
}>;

/* ─────────────────────────────────────────
   Component
 ───────────────────────────────────────── */
export default function CommonSearch({
  placeholder = "Search…",
  value,
  onChange,
  onSubmit,
  onFocus,
  isDark = false,
}: Props) {
  const [focused, setFocused] = useState(false);

  /* Responsive sizing — the bar shrinks on smaller screens so it never
     dominates the layout on mobile/tablet while staying comfortable on desktop. */
  const { select } = useResponsive();
  const dims = {
    minHeight:  select({ mobile: 40, tablet: 42, desktop: 44 }),
    paddingLeft: select({ mobile: 12, tablet: 13, desktop: 14 }),
    gap:        select({ mobile: 8, tablet: 9, desktop: 10 }),
    fontSize:   select({ mobile: 13.5, tablet: 14, desktop: 14 }),
    iconSize:   select({ mobile: 16, tablet: 17, desktop: 18 }),
    submit:     select({ mobile: 28, tablet: 30, desktop: 30 }),
    clear:      select({ mobile: 19, tablet: 20, desktop: 20 }),
  };

  /* Animated values */
  const iconScale  = useRef(new Animated.Value(1)).current;
  const clearScale = useRef(new Animated.Value(0)).current;

  const handleFocus = () => {
    setFocused(true);
    onFocus?.();
    Animated.spring(iconScale, {
      toValue: 1.15,
      useNativeDriver: true,
      tension: 280,
      friction: 18,
    }).start();
  };

  const handleBlur = () => {
    setFocused(false);
    Animated.spring(iconScale, {
      toValue: 1,
      useNativeDriver: true,
      tension: 280,
      friction: 18,
    }).start();
  };

  const handleChange = (text: string) => {
    onChange(text);
    Animated.spring(clearScale, {
      toValue: text.length > 0 ? 1 : 0,
      useNativeDriver: true,
      tension: 320,
      friction: 20,
    }).start();
  };

  const handleClear = () => {
    onChange("");
    Animated.spring(clearScale, {
      toValue: 0,
      useNativeDriver: true,
      tension: 320,
      friction: 20,
    }).start();
  };

  /* Theme tokens */
  const T = isDark ? dark : light;

  // Determine submit button background color
  let submitBgColor = T.submitBg;
  if (focused) {
    submitBgColor = Colors.accent;
  }

  const getWrapperStyles = () => {
    const shadowStyle = (() => {
      if (Platform.OS !== "web") return {};

      if (focused) {
        return {
          boxShadow: isDark
            ? `0 0 0 3px rgba(255,255,255,0.14), 0 4px 24px rgba(0,0,0,0.4)`
            : `0 0 0 3px rgba(10,10,10,0.12), 0 4px 20px rgba(0,0,0,0.06)`
        };
      }

      return {
        boxShadow: isDark
          ? "0 2px 12px rgba(0,0,0,0.35)"
          : "0 2px 12px rgba(0,0,0,0.06)"
      };
    })();

    return [
      styles.wrapper,
      {
        minHeight: dims.minHeight,
        paddingLeft: dims.paddingLeft,
        gap: dims.gap,
        backgroundColor: T.bg,
        borderColor: focused ? T.activeBorder : T.border,
      },
      shadowStyle,
    ];
  };

  const wrapperStyle = getWrapperStyles();


  return (
    <View style={wrapperStyle}>
      {/* Search icon — animates on focus */}
      <Animated.View style={[styles.iconWrap, { transform: [{ scale: iconScale }] }]}>
        <Icon
          name={focused ? "search" : "search-outline"}
          size={dims.iconSize}
          color={focused ? T.activeBorder : T.iconColor}
        />
      </Animated.View>

      {/* Input */}
      <TextInput
        value={value}
        onChangeText={handleChange}
        onSubmitEditing={onSubmit}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        placeholderTextColor={T.placeholder}
        returnKeyType="search"
        underlineColorAndroid="transparent"
        selectionColor={isDark ? "#3B5BF6" : "#6366F1"}
        style={[
          styles.input,
          { color: T.text, fontSize: dims.fontSize, paddingVertical: select({ mobile: 8, tablet: 9, desktop: 10 }) },
          Platform.OS === "web" && ({ outlineStyle: "none" } as any),
        ]}
      />

      {/* Right side actions */}
      <View style={styles.actions}>
        {/* Clear button — springs in/out */}
        <Animated.View style={{ transform: [{ scale: clearScale }], opacity: clearScale }}>
          <TouchableOpacity
            onPress={handleClear}
            hitSlop={{ top: 10, bottom: 10, left: 8, right: 8 }}
            activeOpacity={0.7}
            style={[styles.clearBtn, { width: dims.clear, height: dims.clear, backgroundColor: T.clearBg }]}
          >
            <Icon name="close" size={12} color={T.clearIcon} />
          </TouchableOpacity>
        </Animated.View>

        {/* Divider */}
        {value.length > 0 && (
          <View style={[styles.divider, { backgroundColor: T.divider }]} />
        )}

        {/* Submit arrow */}
        <TouchableOpacity
          onPress={onSubmit}
          activeOpacity={0.65}
          style={[
            styles.submitBtn,
            { width: dims.submit, height: dims.submit, backgroundColor: submitBgColor },
          ]}
        >
          <Icon
            name="arrow-forward"
            size={14}
            color={focused ? "#FFFFFF" : T.submitIcon}
          />
        </TouchableOpacity>
      </View>
    </View>
  );
}

/* ─────────────────────────────────────────
   Theme tokens
───────────────────────────────────────── */
// Mapped to design tokens so the search box follows light/dark mode (tokens
// resolve to CSS variables on web; native keeps the baked light hex).
const light = {
  bg:           Colors.card,
  // Subtle border that blends with the paper background; firms up on focus.
  border:       Colors.line,
  activeBorder: Colors.lineStrong,
  text:         Colors.ink,
  placeholder:  Colors.ink4,
  iconColor:    Colors.ink4,
  clearBg:      Colors.paper2,
  clearIcon:    Colors.ink3,
  divider:      Colors.line,
  submitBg:     Colors.paper2,
  submitIcon:   Colors.ink4,
};

const dark = {
  bg:           "#111827",
  border:       "#1F2937",
  activeBorder: "#3B5BF6",
  text:         "#F9FAFB",
  placeholder:  "#4B5563",
  iconColor:    "#4B5563",
  clearBg:      "#1F2937",
  clearIcon:    "#9CA3AF",
  divider:      "#1F2937",
  submitBg:     "#1F2937",
  submitIcon:   "#6B7280",
};

/* ─────────────────────────────────────────
   Styles
───────────────────────────────────────── */
const styles = StyleSheet.create({
  wrapper: {
    flexDirection: "row",
    alignItems: "center",
    minHeight: 52,
    borderRadius: 2,
    borderWidth: 1.5,
    paddingLeft: 16,
    paddingRight: 8,
    gap: 10,
    ...Platform.select({
      ios: {
        shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.07,
    shadowRadius: 12,
    ...Platform.select({ web: { boxShadow: '0 4px 12px rgba(0,0,0,0.07)' } as any }),
      },
      android: { elevation: 3 },
    }),
  },

  iconWrap: {
    width: 20,
    alignItems: "center",
    justifyContent: "center",
  },

  input: {
    flex: 1,
    fontSize: 15,
    fontWeight: "500",
    paddingVertical: 12,
    backgroundColor: "transparent",
    ...(Platform.OS === "web" ? {
      outlineWidth: 0,
      outlineStyle: "none",
      borderWidth: 0,
    } as any : {}),
  },

  actions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },

  clearBtn: {
    width: 22,
    height: 22,
    borderRadius: 2,
    alignItems: "center",
    justifyContent: "center",
  },

  divider: {
    width: 1,
    height: 18,
    borderRadius: 1,
  },

  submitBtn: {
    width: 34,
    height: 34,
    borderRadius: 2,
    alignItems: "center",
    justifyContent: "center",
    ...Platform.select({
      ios: {
        shadowColor: '#6366F1',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0,
    shadowRadius: 8,
    ...Platform.select({ web: { boxShadow: '0 2px 8px rgba(0,0,0,0)' } as any }),
      },
    }),
  },
});
